import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { useGetStripeMetricsQuery } from '@@/services/stripe';
import { useGetActivityMetricsQuery } from '@@/services/whatsapp';
import { useGetTaskMetricsQuery } from '@@/services/task';

import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
} from 'recharts';

const MetricsPage: React.FC = () => {
  const { data: stripeMetrics } = useGetStripeMetricsQuery();
  const { data: activityMetrics } = useGetActivityMetricsQuery();
  const { data: tasksMetrics } = useGetTaskMetricsQuery();

  const stripeData1 = useMemo(() => {
    if (!stripeMetrics) {
      return [];
    }
    return stripeMetrics.invoices?.map((metric) => {
      return {
        name: moment(
          `${metric._id.month} ${metric._id.year}`,
          'MM YYYY',
        ).format('MMM YY'),
        amountPaid: metric.amountPaid,
        invoices: metric.customers,
      };
    });
  }, [stripeMetrics]);

  const totalPayment = useMemo(
    () =>
      stripeMetrics?.invoices.reduce((accum, inv) => {
        return accum + inv.amountPaid;
      }, 0),
    [stripeMetrics],
  );

  const stripeData2 = useMemo(() => {
    if (!stripeMetrics) {
      return [];
    }
    return stripeMetrics.subscriptions?.map((metric) => {
      return {
        name: metric._id,
        value: metric.count,
        fill: metric._id === 'active' ? '#8884d8' : '#FF0000',
      };
    });
  }, [stripeMetrics]);

  const nbCustomers = useMemo(
    () =>
      stripeMetrics?.subscriptions.reduce((accum, sub) => {
        return sub._id === 'active' ? accum + sub.count : accum;
      }, 0),
    [stripeMetrics],
  );

  const churnedUsers = useMemo(() => {
    return (
      stripeMetrics?.subscriptions?.find((s) => s._id === 'canceled')?.users ||
      []
    );
  }, [stripeMetrics]);

  const activityData = useMemo(() => {
    if (!activityMetrics) {
      return [];
    }
    return activityMetrics.messages.map((metric, i) => {
      return {
        name: moment(
          `${metric._id.month} ${metric._id.year}`,
          'MM YYYY',
        ).format('MMM YY'),
        messages: metric.count,
        documents: activityMetrics.documents[i]?.count || 0,
      };
    });
  }, [activityMetrics]);

  const totalMessages = useMemo(
    () =>
      activityMetrics?.messages.reduce((accum, month) => {
        return accum + month.count;
      }, 0),
    [activityMetrics],
  );

  const totalDocuments = useMemo(
    () =>
      activityMetrics?.documents.reduce((accum, month) => {
        return accum + month.count;
      }, 0),
    [activityMetrics],
  );

  const taskData = useMemo(() => {
    if (!tasksMetrics) {
      return [];
    }
    return tasksMetrics.map((metric) => {
      return {
        name: moment(
          `${metric._id.month} ${metric._id.year}`,
          'MM YYYY',
        ).format('MMM YY'),
        done: metric.done,
      };
    });
  }, [tasksMetrics]);

  const totalTasksCompleted = useMemo(
    () =>
      tasksMetrics?.reduce((accum, month) => {
        return accum + month.done;
      }, 0),
    [tasksMetrics],
  );

  return (
    <div className="relative p-4 w-full">
      <div className="mb-6 font-main text-main ">
        <p className="underline text-lg">Evolution Paiements x Factures</p>

        <p className="mt-1">
          Montant total perçu :&nbsp;
          {totalPayment?.toLocaleString('fr', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}
        </p>
      </div>
      <LineChart
        width={1000}
        height={400}
        data={stripeData1}
        margin={{
          top: 5,
          right: 5,
          left: 5,
          bottom: 30,
        }}
      >
        <CartesianGrid stroke="#ccc" strokeDasharray="3 3" />
        <XAxis dataKey="name" angle={-45} textAnchor="end" />
        <YAxis yAxisId="left" />
        <YAxis yAxisId="right" orientation="right" />
        <Tooltip />
        <Legend layout="vertical" verticalAlign="top" align="right" />

        <Line
          type="monotone"
          dataKey="amountPaid"
          stroke="#8884d8"
          yAxisId="left"
        />
        <Line
          type="monotone"
          dataKey="invoices"
          stroke="#FF0000"
          yAxisId="right"
        />
      </LineChart>
      <p className="mt-12 mb-6 font-main text-main underline text-lg">
        Souscriptions ({nbCustomers} actives)
      </p>
      <PieChart width={250} height={250}>
        <Pie
          data={stripeData2}
          dataKey="value"
          nameKey="name"
          outerRadius={100}
          label
        />
        <Tooltip />
      </PieChart>
      {!!churnedUsers && (
        <div className="m-2">
          <h2 className="underline text-main mb-2">Churns :</h2>
          <div className="flex flex-row items-center flex-wrap">
            {churnedUsers.map((user) => {
              return (
                <Link
                  className="p-2 m-0.5 bg-gray-200 rounded-md"
                  to={`/patients/${user._id}`}
                  key={user._id}
                >
                  <p className="text-sm underline">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="text-xs">
                    {moment.unix(user.subscriptionStartedAt).format('MM/YYYY')}
                    &nbsp;-&nbsp;
                    {user.subscriptionCanceledAt
                      ? moment
                          .unix(user.subscriptionCanceledAt)
                          .format('MM/YYYY')
                      : '???'}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      )}
      <p className="mt-12 mb-6 font-main text-main underline text-lg">
        {totalMessages?.toLocaleString('fr', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}
        &nbsp;Messages et&nbsp;
        {totalDocuments?.toLocaleString('fr', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}
        &nbsp;Documents
      </p>
      <LineChart
        width={1000}
        height={400}
        data={activityData}
        margin={{
          top: 5,
          right: 5,
          left: 5,
          bottom: 30,
        }}
      >
        <CartesianGrid stroke="#ccc" strokeDasharray="3 3" />
        <XAxis dataKey="name" angle={-45} textAnchor="end" />
        <YAxis yAxisId="left" />
        <YAxis yAxisId="right" orientation="right" />
        <Tooltip />
        <Legend layout="vertical" verticalAlign="top" align="right" />

        <Line
          type="monotone"
          dataKey="messages"
          stroke="#8884d8"
          yAxisId="left"
        />
        <Line
          type="monotone"
          dataKey="documents"
          stroke="#FF0000"
          yAxisId="right"
        />
      </LineChart>
      <p className="mt-12 mb-6 font-main text-main underline text-lg">
        {totalTasksCompleted?.toLocaleString('fr', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}
        &nbsp;tâches complétées
      </p>
      <LineChart
        width={1000}
        height={400}
        data={taskData}
        margin={{
          top: 5,
          right: 5,
          left: 5,
          bottom: 30,
        }}
      >
        <CartesianGrid stroke="#ccc" strokeDasharray="3 3" />
        <XAxis dataKey="name" angle={-45} textAnchor="end" />
        <YAxis yAxisId="left" />

        <Tooltip />
        <Legend layout="vertical" verticalAlign="top" align="right" />

        <Line type="monotone" dataKey="done" stroke="#8884d8" yAxisId="left" />
      </LineChart>
      <div className="h-16" />
    </div>
  );
};

export default MetricsPage;
